import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  orderBy,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from '../auth/firebase';
import { TaskItem } from '@rsmaids/shared/types/tasks';

const TASKS_COLLECTION = 'tasks';

/**
 * Subscribe to tasks assigned to a specific staff user (real-time).
 */
export function subscribeToMyTasks(
  assignedToId: string,
  onData: (tasks: TaskItem[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, TASKS_COLLECTION),
    where('assignedToId', '==', assignedToId),
    orderBy('createdAt', 'desc'),
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const tasks: TaskItem[] = snapshot.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          title: data.title ?? '',
          description: data.description,
          dueDate: data.dueDate ?? null,
          priority: data.priority ?? 'medium',
          status: data.status ?? 'pending',
          completedAt: data.completedAt ?? null,
          createdAt: data.createdAt?.toDate?.()?.toISOString() ?? '',
          updatedAt: data.updatedAt?.toDate?.()?.toISOString(),
          notes: data.notes,
          contactId: data.contactId,
          assignedToId: data.assignedToId,
          assignedToName: data.assignedToName,
          assignedById: data.assignedById,
          assignedByName: data.assignedByName,
        } as TaskItem;
      });
      onData(tasks);
    },
    (error) => onError?.(error),
  );
}

/**
 * Mark a task as completed.
 */
export async function completeTask(taskId: string): Promise<void> {
  await updateDoc(doc(db, TASKS_COLLECTION, taskId), {
    status: 'completed',
    completedAt: new Date().toISOString(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Reopen a completed task back to pending.
 */
export async function reopenTask(taskId: string): Promise<void> {
  await updateDoc(doc(db, TASKS_COLLECTION, taskId), {
    status: 'pending',
    completedAt: null,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Create a new task (called by manager/owner).
 */
export async function createTask(task: Omit<TaskItem, 'id' | 'createdAt' | 'updatedAt' | 'completedAt' | 'status'>): Promise<string> {
  const ref = await addDoc(collection(db, TASKS_COLLECTION), {
    ...task,
    status: 'pending',
    completedAt: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}
